import {Popover, Transition} from '@headlessui/react';
import {writePublishData} from '../../../firebase_utils';
import React, {useReducer, useState} from 'react';
import Button from 'Components/Tailwind/Button';

interface Props {
    pageId: string;
    projectId: string;
}

type State = {
    status: 'idle' | 'loading' | 'success' | 'error';
    errorMessage: string | null;
};

type Action = {type: 'idle'} | {type: 'loading'} | {type: 'success'} | {type: 'error'; payload: string};

const initialState: State = {
    status: 'idle',
    errorMessage: null,
};

const requestReducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'idle':
            return {status: 'idle', errorMessage: null};
        case 'loading':
            return {status: 'loading', errorMessage: null};
        case 'success':
            return {status: 'success', errorMessage: null};
        case 'error':
            return {status: 'error', errorMessage: action.payload};
        default:
            return state;
    }
};

export const PublishDialog: React.FC<Props> = (props: Props) => {
    const [input, setInput] = useState('');
    const [state, dispatch] = useReducer(requestReducer, initialState);

    const getResourceId = (name: string) =>
        `${props.projectId}|${name.replaceAll(' ', '').replaceAll('/', '').replaceAll('|', '')}`;

    const onSubmitPublishSite = async (name: string) => {
        // Store on server an alias for the site, then open in a new tab.
        const resourceId = getResourceId(name);
        dispatch({type: 'loading'});
        try {
            await writePublishData(resourceId, props.pageId);
        } catch (e) {
            if (e instanceof Error) {
                dispatch({type: 'error', payload: e.message});
            }
        }

        dispatch({type: 'success'});
        window.open(`${window.location.origin}/publish/${resourceId.replaceAll('|', '/')}`);
    };

    const onSuccessClick = async (name: string) => {
        // Store on server an alias for the site, then open in a new tab.
        const resourceId = getResourceId(name);
        window.open(`${window.location.origin}/publish/${resourceId.replaceAll('|', '/')}`);
    };

    return (
        <Popover className="relative w-full">
            {({open}) => (
                <>
                    <Popover.Button
                        as={Button}
                        outlined
                        onClick={() => {
                            setInput('');
                            dispatch({type: 'idle'});
                        }}
                        className="w-full py-2.5"
                    >
                        Publish
                    </Popover.Button>
                    <Transition
                        as={React.Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                    >
                        <Popover.Panel className="absolute right-0 z-50 mt-3">
                            {state.status === 'idle' && (
                                <div className="flex w-72 flex-col space-y-2 overflow-hidden rounded-lg bg-neutral-700 p-4 shadow-lg ring-1 ring-black ring-opacity-5">
                                    <h1 className="text-base font-black text-white">Publish</h1>
                                    <input
                                        placeholder="Enter the page name"
                                        value={input}
                                        onInput={(e) => setInput(e.currentTarget.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                onSubmitPublishSite(input);
                                            }
                                        }}
                                        className="inline-flex flex-col justify-between rounded p-2 font-normal text-black"
                                    />
                                    <Button onClick={() => onSubmitPublishSite(input)}>Confirm Publish</Button>
                                </div>
                            )}
                            {state.status === 'loading' && (
                                <div className="flex w-72 flex-col space-y-2 overflow-hidden rounded-lg bg-neutral-600 p-4 shadow-lg ring-1 ring-black ring-opacity-5">
                                    <h1 className="text-base font-black text-white">Loading...</h1>
                                </div>
                            )}
                            {state.status === 'success' && (
                                <div className="flex w-72 flex-col space-y-2 overflow-hidden rounded-lg bg-neutral-600 p-4 shadow-lg ring-1 ring-black ring-opacity-5">
                                    <h1 className="text-base font-black text-white">Success</h1>
                                    <Button onClick={() => onSuccessClick(input)}>Click to open</Button>
                                </div>
                            )}
                            {state.status === 'error' && (
                                <div className="flex w-72 flex-col space-y-2 overflow-hidden rounded-lg bg-neutral-600 p-4 shadow-lg ring-1 ring-black ring-opacity-5">
                                    <h1 className="text-base font-black text-white">Error: ${state.errorMessage}</h1>
                                </div>
                            )}
                        </Popover.Panel>
                    </Transition>
                </>
            )}
        </Popover>
    );
};
